import styled, { css } from 'styled-components';

import { ModalCloseButton as BaseModalCloseButton } from '@components/Modal/next/Modal/utils';
import HorizontalScrollableList, {
  hideScrollbarMixin,
} from '@components/utils/HorizontalScrollableList';

const ArrowsStyles = {
  next: css`
    transform: translateX(100%);
  `,
  prev: css`
    transform: translateX(-100%);
  `,
};
export const Arrow = styled.button<{ direction: 'prev' | 'next' }>`
  width: 38px;
  height: 38px;
  border-style: none;
  cursor: pointer;
  background-color: ${(p) => p.theme.palette.white};
  opacity: 0.5;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${(p) => ArrowsStyles[p.direction]};
  svg {
    height: 18px;
    width: 18px;
  }
`;

const delta = 20;
export const NavArrowsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  position: relative;
  left: ${-delta / 2}px;
  width: calc(100% + ${delta}px);
  position: absolute;
  bottom: 15px;
  ${Arrow} {
    pointer-events: auto;
  }
`;

export const GalleryListItemTitle = styled.span`
  cursor: pointer;
  color: ${(p) => p.theme.palette.darkGray};
  font-size: 14px;
  background: ${(p) => p.theme.palette.white};
  padding: 13px 20px;
  white-space: nowrap;
  height: 44px;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
`;
export const VideoWrapper = styled.div`
  margin-right: 4px;
`;
export const HorizontalScrollableGalleriesList = styled(
  HorizontalScrollableList
)<{
  activeItem: string;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: scroll;
  ${hideScrollbarMixin};
  ${GalleryListItemTitle} {
    &[data-item='${(p) => p.activeItem}'] {
      border-bottom: solid 3px ${(p) => p.theme.palette.tomato};
      color: ${(p) => p.theme.palette.coral};
      font-weight: 600;
    }
  }
`;

export const GalleryListNavArrowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  position: relative;
  left: ${-delta / 2}px;
  width: calc(100% + ${delta}px);
  position: absolute;
  top: 63px;
  ${Arrow} {
    pointer-events: auto;
  }

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    display: none;
  }
`;

export const Title = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: ${(p) => p.theme.palette.white};
  line-height: 1;
`;

export const CurrentSlideInfoDesktop = styled.span`
  min-width: 194px;
  height: 35px;
  padding: 0 20px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.palette.gray87};
  color: ${(p) => p.theme.palette.darkGray};
  position: absolute;
  top: 144px;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  span {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    &:first-child {
      padding-right: 10px;
      flex: 1;
    }
    &:not(:first-child) {
      border-left: solid 1px ${(p) => p.theme.palette.lightBlueGrey};
      padding-left: 10px;
    }
  }
`;
export const CurrentSlideInfoMobile = styled.span`
  min-width: 194px;
  padding-left: 16px;
  border-radius: 8px;
  color: ${(p) => p.theme.palette.white};
  position: absolute;
  top: 16px;
  z-index: 10;
  display: none;
`;

export const CloseImageViewerButton = styled(BaseModalCloseButton)`
  display: none;
`;

export const ImageViewer = styled.div<{
  horizontalPadding?: `${number}%`;
  isFullScreen?: boolean;
  isVideoTab?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
  padding: ${(p) =>
    p.horizontalPadding ? `0 ${p.horizontalPadding}` : undefined};
  &&&& {
    .image-gallery-image {
      max-height: 370px;
      height: 370px;
      @media only screen and (max-height: 699px) {
        height: 350px;
      }
      @media only screen and (max-height: 570px) {
        height: 250px;
      }
    }
    .image-gallery-slide-wrapper {
      background-color: ${(p) => p.theme.palette.black};
      margin-bottom: 40px;
      @media ${({ theme }) => `(min-width: ${theme.breakPoint.LARGE_1X})`} {
        border-radius: 8px;
      }
    }
    .image-gallery-slide .image-gallery-description {
      background: ${({ theme }) => theme.palette.transparent};
      bottom: 0px;
      border-radius: 8px;
      color: ${({ theme }) => theme.palette.white};
      left: 0;
      font-size: 16px;
      line-height: 1;
      padding: 10px 20px;
      width: 100%;
      text-align: left;
      position: absolute;
      white-space: normal;
    }
    .image-gallery-thumbnails {
      padding: 0;
    }

    .image-gallery-thumbnail {
      width: 142px;
      height: 70px;
      border-radius: 8px;
      &.active,
      &:focus,
      &:hover {
        border: solid 3px ${(p) => p.theme.palette.coral};
      }
      & + .image-gallery-thumbnail {
        margin-left: 10px;
      }
      .image-gallery-thumbnail-inner {
        height: 100%;
        border-radius: 8px;
        background-color: ${({ theme }) => theme.palette.black};
        .image-gallery-thumbnail-image {
          max-width: 100%;
          width: initial !important;
          max-height: 100%;
          height: 70px;
        }
      }
    }
  }
  ${CurrentSlideInfoDesktop} {
    right: ${(p) => (p.horizontalPadding ? p.horizontalPadding : 0)};
  }
  ${NavArrowsContainer} {
    padding: ${(p) =>
      p.horizontalPadding ? `0 ${p.horizontalPadding}` : undefined};
  }

  ${GalleryListNavArrowContainer} {
    padding: ${(p) =>
      p.horizontalPadding ? `0 ${p.horizontalPadding}` : undefined};
  }

  ${(p) => p.theme.mediaQueries.GALLERY_SMALL_SCREEN} {
    padding: 16px 0 20px;
    height: 100%;
    gap: 0;
    ${CurrentSlideInfoDesktop} ,  ${NavArrowsContainer} , ${HorizontalScrollableGalleriesList}, ${Title} {
      display: none;
    }
    ${CloseImageViewerButton} {
      display: block;
      position: absolute;
      top: 16px;
      right: 20px;
    }
    .image-gallery {
      flex: 1;
      display: flex;
      flex-direction: column;
      margin-top: 60px;
      .image-gallery-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        justify-content: space-evenly;
      }
    }
    ${CurrentSlideInfoMobile} {
      display: initial;
    }
  }

  @media ${({ theme }) => `(min-width: ${theme.breakPoint.LARGE_1X})`} {
    ${({ isFullScreen }) =>
      isFullScreen &&
      `
    
    .image-gallery-content,
    .image-gallery-slide-wrapper,
    .image-gallery-swipe {
      height: 100vh;
    }

    .image-gallery-slides,
    .image-gallery-slide {
      height: 100%;
    }
    
    
    `}
  }
`;

export const GalleryHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FullScreenButtonWrapper = styled.div`
  position: relative;
  right: -7%;
  display: flex;
  gap: 20px;
`;
